import { Vector3 } from '@technology/web3d';
import {
  CADPoint,
  CADStyle,
  CompoundCurve,
  ICADObject,
  LineSegment,
  LineType,
} from '@technology/web3d-plugin-line-editing';
import type { GeoJsonFeature } from '@trimble/web3d-plugin-features-service';
import { UnreachableCaseError } from 'ts-essentials';
import { stampFeatureLink } from '../interfaces/cad-object-metadata';
import type { GeospatialFeature } from '../types/geospatial-feature';
import {
  childGeospatialLogger,
  type GeospatialLogger,
} from '../logging/geospatial-logger';

export interface FeatureCadTransformerOptions {
  readonly projector: CoordinateProjector;
  readonly logger: GeospatialLogger;
  readonly elevationSampler?: ElevationSampler;
  readonly pointColor?: string;
  readonly lineColor?: string;
}

export interface ScenePosition {
  x: number;
  y: number;
  z?: number;
}

export type CoordinateProjector = (lon: number, lat: number) => ScenePosition;

type FeatureCoordinate = readonly [number, number, number?];

/** Nearest point-cloud Z at scene X,Y, or undefined when unavailable. */
export type ElevationSampler = (x: number, y: number) => number | undefined;

const DEFAULT_POINT_COLOR = 'red';
const DEFAULT_LINE_COLOR = 'blue';
const POINT_LINE_WIDTH = 2;
const LINE_WIDTH = 2;
const POINT_SIZE = 10;
const MIN_LINE_STRING_POINTS = 2;
const MIN_POLYGON_RING_COORDINATES = 4;
const MIN_XY_COORDINATE_LENGTH = 2;
const LONGITUDE = 0;
const LATITUDE = 1;
const ELEVATION = 2;
/** Lift markers slightly above sampled surface so they are not buried. */
const SURFACE_ELEVATION_OFFSET = 1;

function hasFiniteXY(coordinates: FeatureCoordinate): boolean {
  return (
    coordinates.length >= MIN_XY_COORDINATE_LENGTH &&
    Number.isFinite(coordinates[LONGITUDE]) &&
    Number.isFinite(coordinates[LATITUDE])
  );
}

/** Pure FS GeoJSON feature → CAD object (no event subscription). */
export class FeatureCadTransformer {
  private readonly projector: CoordinateProjector;
  private readonly elevationSampler?: ElevationSampler;
  private readonly pointColor: string;
  private readonly lineColor: string;
  private readonly logger: GeospatialLogger;

  constructor(options: FeatureCadTransformerOptions) {
    this.projector = options.projector;
    this.elevationSampler = options.elevationSampler;
    this.pointColor = options.pointColor ?? DEFAULT_POINT_COLOR;
    this.lineColor = options.lineColor ?? DEFAULT_LINE_COLOR;
    this.logger = childGeospatialLogger(
      options.logger,
      'FeatureCadTransformer'
    );
  }

  /**
   * Backend-agnostic {@link GeospatialFeature} → CAD object, so hosts can mirror
   * the shared feature map (not just streamed GeoJSON) into a line-editing
   * model. Geometry is treated as WGS-84 lon/lat, projected via the projector.
   */
  public toCadObjectFromFeature(
    feature: GeospatialFeature
  ): ICADObject | undefined {
    if (!feature.id) {
      return undefined;
    }
    return this.toCadObject({
      type: 'Feature',
      id: feature.id,
      geometry: feature.geometry,
      properties: { ...feature.properties },
      metadata: { ...feature.metadata },
    });
  }

  public toCadObject(feature: GeoJsonFeature): ICADObject | undefined {
    switch (feature.geometry.type) {
      case 'Point':
        return this.toPoint(feature, feature.geometry.coordinates);
      case 'LineString':
        return this.toCompoundCurve(feature, feature.geometry.coordinates);
      case 'Polygon':
        return this.toCompoundCurveFromPolygon(
          feature,
          feature.geometry.coordinates
        );
      default:
        throw new UnreachableCaseError(feature.geometry);
    }
  }

  private toPoint(
    feature: GeoJsonFeature,
    coordinates: FeatureCoordinate
  ): ICADObject | undefined {
    if (!hasFiniteXY(coordinates)) {
      this.logger.error('Skipped Point transform (invalid coordinates)', {
        featureId: feature.id,
      });
      return undefined;
    }
    const projected = this.projector(
      coordinates[LONGITUDE],
      coordinates[LATITUDE]
    );
    const elevation = this.resolveElevation(coordinates[ELEVATION], projected);
    const style = new CADStyle(
      this.pointColor,
      POINT_LINE_WIDTH,
      LineType.Solid,
      POINT_SIZE
    );
    return new CADPoint(
      new Vector3(projected.x, projected.y, elevation),
      style,
      this.buildMetaData(feature),
      feature.id,
      true
    );
  }

  private toCompoundCurveFromPolygon(
    feature: GeoJsonFeature,
    rings: readonly FeatureCoordinate[][]
  ): ICADObject | undefined {
    const exterior = rings[0];
    if (!exterior) {
      this.logger.error('Skipped Polygon transform (missing exterior ring)', {
        featureId: feature.id,
      });
      return undefined;
    }
    // A CompoundCurve is one connected curve, so it cannot carry holes. Mirroring
    // the exterior alone would let an edit persist the ring back without them.
    if (rings.length > 1) {
      this.logger.debug('Skipped Polygon transform (holes cannot be edited)', {
        featureId: feature.id,
        holeCount: rings.length - 1,
      });
      return undefined;
    }
    const ring = this.closedRingCoordinates(exterior);
    if (ring.length < MIN_POLYGON_RING_COORDINATES) {
      this.logger.error('Skipped Polygon transform (too few ring vertices)', {
        featureId: feature.id,
        pointCount: ring.length,
      });
      return undefined;
    }
    return this.toCompoundCurve(feature, ring);
  }

  private closedRingCoordinates(
    ring: readonly FeatureCoordinate[]
  ): FeatureCoordinate[] {
    if (ring.length === 0) {
      return [];
    }
    const first = ring[0];
    const last = ring[ring.length - 1];
    const alreadyClosed =
      first[LONGITUDE] === last[LONGITUDE] &&
      first[LATITUDE] === last[LATITUDE] &&
      first[ELEVATION] === last[ELEVATION];
    return alreadyClosed ? [...ring] : [...ring, first];
  }

  private toCompoundCurve(
    feature: GeoJsonFeature,
    coordinates: readonly FeatureCoordinate[]
  ): ICADObject | undefined {
    if (coordinates.length < MIN_LINE_STRING_POINTS) {
      this.logger.error('Skipped LineString transform (too few points)', {
        featureId: feature.id,
        pointCount: coordinates.length,
      });
      return undefined;
    }
    if (!coordinates.every(hasFiniteXY)) {
      this.logger.error('Skipped LineString transform (invalid coordinates)', {
        featureId: feature.id,
      });
      return undefined;
    }
    const points = coordinates.map((coord) => {
      const projected = this.projector(coord[LONGITUDE], coord[LATITUDE]);
      const elevation = this.resolveElevation(coord[ELEVATION], projected);
      return new Vector3(projected.x, projected.y, elevation);
    });
    const segments: LineSegment[] = [];
    for (let i = 0; i < points.length - 1; i++) {
      segments.push(new LineSegment(points[i], points[i + 1]));
    }
    const style = new CADStyle(this.lineColor, LINE_WIDTH, LineType.Solid);
    return new CompoundCurve(
      style,
      segments,
      this.buildMetaData(feature),
      feature.id
    );
  }

  private buildMetaData(feature: GeoJsonFeature): Record<string, unknown> {
    const metaData = { ...(feature.metadata ?? {}) };
    stampFeatureLink(metaData, feature.id, feature);
    return metaData;
  }

  private resolveElevation(
    explicit: number | undefined,
    projected: ScenePosition
  ): number {
    if (explicit !== undefined) {
      return explicit;
    }
    const surface = this.elevationSampler?.(projected.x, projected.y);
    if (surface !== undefined) {
      return surface + SURFACE_ELEVATION_OFFSET;
    }
    return projected.z ?? 0;
  }
}
